// Format date string 'YYYY-MM-DD' to 'DD MMM YYYY'
function formatDisplayDate(dateStr) {
  if (!dateStr) return '';
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return dateStr;
  return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
}
import { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { Modal, Button, Spinner } from "react-bootstrap"
import Select from "react-select";
import {
  createFollowUpAction,
  getFollowUpAction,
} from "../../../../../store/actions/FollowUpAction"
import {
  showErrorAlert,
  handleSuccess,
} from "../../../../../services/ErrorHandlingService"
import {
  getCallLogs,
  addCallLog,
  updateCallLog,
  deleteCallLog,
  formatCallLogs,
} from "../../../../../services/CallLogsService"
import { FaMicrophone } from "react-icons/fa";
import { useRef } from "react";
import { formatDate } from "../../../../../utils/dateUtils"
const CallLogs = ({ leadId }) => {
    const [callLogs, setCallLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);

    // Fetch call logs
    const fetchCallLogs = async () => {
      setLogsLoading(true);
      try {
        const response = await getCallLogs(leadId);
        if (response?.status === 200 && response.data?.status === "success") {
          const logs = formatCallLogs(response.data.data);
          setCallLogs(logs);
        } else {
          setCallLogs([]);
        }
      } catch (error) {
        console.error("Error fetching call logs:", error);
        setCallLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };

    // Load call logs on mount and when leadId changes
    useEffect(() => {
      if (leadId) fetchCallLogs();
    }, [leadId]);

    // Declare showModal state before using in prevShowModal
    const [showModal, setShowModal] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [editingLogId, setEditingLogId] = useState(null)

    const dispatch = useDispatch()
    const [submitting, setSubmitting] = useState(false)

    const [note, setNote] = useState("")
    const [leadType, setLeadType] = useState("")
    const [callType, setCallType] = useState("")
    const [showFollowUpSection, setShowFollowUpSection] = useState(false)
    const [isRecording, setIsRecording] = useState(false);
    const [speechLang, setSpeechLang] = useState("en-IN"); 
    const [formData, setFormData] = useState({
      reason: "",
      next_action: "",
      next_follow_up_date: "",
      next_followup_time: "", // NEW: reminder time field
    })
    const recognitionRef = useRef(null);

    const recognition = new SpeechRecognition();

  const toggleLanguage = () => {
    setSpeechLang((prev) => (prev === "en-IN" ? "mr-IN" : "en-IN"));
  };
  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
  
    if (!SpeechRecognition) {
      alert("Your browser does not support Speech Recognition");
      return;
    }
  
    // 🚫 already recording
    if (isRecording) return;
  
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
  
    recognition.lang = speechLang;
    recognition.continuous = false;
    recognition.interimResults = false;
  
    setIsRecording(true);
  
    recognition.onresult = (event) => {
      const spokenText = event.results[0][0].transcript;
  
      setNote((prev) =>
        prev ? prev + " " + spokenText : spokenText
      );
    };
  
    recognition.onerror = (event) => {
      console.warn("Speech recognition error:", event.error);
  
      // aborted is normal → ignore
      if (event.error !== "aborted") {
        alert("Speech recognition failed. Try again.");
      }
  
      setIsRecording(false);
    };
  
    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };
  
    recognition.start();
  };
  
  const handleModalClose = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  
    setIsRecording(false);
    setShowModal(false);
    setIsEditing(false);
    setEditingLogId(null);
    setShowFollowUpSection(false);
    setNote(""); // reset note
    setFormData({
      reason: "",
      next_action: "",
      next_follow_up_date: "",
      next_followup_time: "",
    });
    setFieldErrors({
      note: false,
      leadType: false,
      callType: false,
      reason: false,
      next_action: false,
      next_follow_up_date: false,
      next_followup_time: false,
    });
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Validation error state
  const [fieldErrors, setFieldErrors] = useState({
    note: false,
    leadType: false,
    callType: false,
    reason: false,
    next_action: false,
    next_follow_up_date: false,
    next_followup_time: false,
  });

  const handleSubmitFollowUp = async (e) => {
    e.preventDefault();
    if (!leadId) return showErrorAlert("Lead ID missing!", "error");

    // Validation for required fields (set error state instead of alert)
    const errors = {
      note: !note.trim(),
      leadType: !leadType,
      callType: !callType,
      reason: showFollowUpSection ? !formData.reason.trim() : false,
      next_action: showFollowUpSection ? !formData.next_action.trim() : false,
      next_follow_up_date: showFollowUpSection ? !formData.next_follow_up_date : false,
      // next_followup_time is optional, no validation needed
    };
    setFieldErrors(errors);
    // If any error, do not submit
    if (Object.values(errors).some(Boolean)) return;

    setSubmitting(true);

    try {
      const isUpdate = isEditing && editingLogId;
      const payload = {
        lead_id: leadId,
        call_note: note,
        lead_type: leadType,
        call_type: callType,
      };

      if (isUpdate) {
        payload.call_log_id = editingLogId;
      }

      // Only add follow-up fields if follow-up section is shown
      if (showFollowUpSection) {
        payload.followup_reason = formData.reason;
        payload.next_action = formData.next_action;
        payload.next_followup_date = formData.next_follow_up_date;
        payload.next_followup_time = formData.next_followup_time; // NEW: include reminder time
      }

      const response = isUpdate 
        ? await updateCallLog(payload)
        : await addCallLog(payload);

      if (response?.status === 200 && (response.data?.success || response.data?.status === "success")) {
        handleSuccess(isUpdate ? "Call Log Updated ✅" : "Call Log Added ✅");
        await fetchCallLogs(); 
        if (showFollowUpSection) {
          await dispatch(getFollowUpAction(leadId));
        }// reload list after add
        handleModalClose();
      } else {
        showErrorAlert(response.data?.message || (isUpdate ? "Failed to update call log" : "Failed to add call log"), "error");
      }
    } catch (err) {
      console.error("Error saving call log:", err);
      showErrorAlert(isEditing ? "Failed to update call log" : "Failed to add call log", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = async (log) => {
    const callLogId = log.call_log_id || log.id;
    if (!callLogId) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this call log?");
    if (!confirmDelete) return;

    try {
      const response = await deleteCallLog(callLogId);

      if (response?.status === 200 && (response.data?.success || response.data?.status === "success")) {
        handleSuccess("Call Log Deleted ✅");
        await fetchCallLogs();
      } else {
        showErrorAlert(response.data?.message || "Failed to delete call log", "error");
      }
    } catch (err) {
      showErrorAlert("Failed to delete call log", "error");
    }
  };

  const handleEditClick = (log) => {
    setIsEditing(true);
    setEditingLogId(log.call_log_id || log.id || null);
    setNote(log.call_note || "");
    setLeadType(log.lead_type || "");
    setCallType(log.call_type || "");
    const hasFollowUp = Boolean(log.followup_reason || log.next_action || log.next_followup_date || log.next_followup_time);
    setShowFollowUpSection(hasFollowUp);
    setFormData({
      reason: log.followup_reason || "",
      next_action: log.next_action || "",
      next_follow_up_date: log.next_followup_date || "",
      next_followup_time: log.next_followup_time || "",
    });
    setFieldErrors({
      note: false,
      leadType: false,
      callType: false,
      reason: false,
      next_action: false,
      next_follow_up_date: false,
      next_followup_time: false,
    });
    setShowModal(true);
  };
  
  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: "38px",
      fontSize: "14px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
      fontSize: "14px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "14px",
    }),
  };
  const handleRemoveFollowUp = () => {
    // ❌ Hide section
    setShowFollowUpSection(false);
  
    // 🔥 Clear form data
    setFormData({
      reason: "",
      next_action: "",
      next_follow_up_date: "",
      next_followup_time: "",
    });
  
    // 🔥 Clear errors
    setFieldErrors((prev) => ({
      ...prev,
      reason: false,
      next_action: false,
      next_follow_up_date: false,
      next_followup_time: false,
    }));
  };
  const leadTypeOptions = [
    { value: "Warm", label: "Warm" },
    { value: "Hot", label: "Hot" },
    { value: "Cold", label: "Cold" },
  ];

  const callTypeOptions = [
    { value: "Ignored", label: "Ignored" },
    { value: "Not Received", label: "Not Received" },
    { value: "Positive", label: "Positive" },
    { value: "Neutral", label: "Neutral" },
  ];

  return (
    <div className="card shadow-none">
      <div className="card-body">
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => {
              setIsEditing(false);
              setEditingLogId(null);
              setShowFollowUpSection(false);
              setNote("");
              setLeadType("");
              setCallType("");
              setFormData({
                reason: "",
                next_action: "",
                next_follow_up_date: "",
                next_followup_time: "",
              });
              setFieldErrors({
                note: false,
                leadType: false,
                callType: false,
                reason: false,
                next_action: false,
                next_follow_up_date: false,
                next_followup_time: false,
              });
              setShowModal(true);
            }}
            className="me-2"
          >
            + Add Call Log
          </Button>
        </div>

        {/* Call Logs List */}
        <div className="mb-4">
          {logsLoading ? (
            <div className="text-muted">Loading call logs...</div>
          ) : callLogs.length === 0 ? (
            <div className="text-muted">No call logs found.</div>
          ) : (
            <div className="contacts_body p-0 dz-scroll" style={{ maxHeight: 350, overflowY: 'auto' }}>
              <ul className="contacts" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {callLogs.map((log, idx) => (
                  <li
                    key={log.call_log_id || log.id || idx}
                    className="active"
                    style={{
                      background: '#f7f8fa',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      padding: '18px 22px',
                      border: 'none',
                      boxShadow: 'none',
                      display: 'block',
                    }}
                  >
                    <div className="d-flex bd-highlight align-items-center">
                      <div className="user_info">
                        <span style={{ fontWeight: 600, color: '#23272b' }}>{log.call_note || <em>No note</em>}</span>
                        <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>
                          {log.lead_type && <span><strong>Lead Type:</strong> {log.lead_type} &nbsp;</span>}
                          {log.call_type && <span><strong>Call Type:</strong> {log.call_type}</span>}
                        </div>
                        {log.followup_reason && (
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            <strong>Follow-up Reason:</strong> {log.followup_reason}
                          </div>
                        )}
                        {log.next_action && (
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            <strong>Next Action:</strong> {log.next_action}
                          </div>
                        )}
                        {log.next_followup_date && (
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            <strong>Next Follow-Up Date:</strong> {formatDate(log.next_followup_date)}
                          </div>
                        )}
                        {log.next_followup_time && (
                          <div style={{ fontSize: 13, color: '#888', marginTop: 2 }}>
                            <strong>Reminder Time:</strong> {log.next_followup_time}
                          </div>
                        )}
                      </div>
                      <div className="ms-auto d-flex align-items-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-1"
                          style={{ padding: '2px 8px' }}
                          onClick={() => handleEditClick(log)}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          style={{ padding: '2px 8px' }}
                          onClick={() => handleDeleteClick(log)}
                        >
                          <i className="fa fa-trash"></i>
                        </Button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {/* Single Combined Modal */}
        <Modal show={showModal} onHide={handleModalClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>{isEditing ? "Edit Call Log" : "Add Call Log"}</Modal.Title>
          </Modal.Header>

          <form onSubmit={handleSubmitFollowUp}>
            <Modal.Body>
              {/* 🟦 Call Log Section */}
              <h6 className="fw-bold text-primary mb-3">Call Details</h6>

              <div className="position-relative">
                <label className="form-label">Note</label>
                <textarea
                  className={`form-control${fieldErrors.note ? ' is-invalid' : ''}`}
                  style={{ marginBottom: fieldErrors.note ? 0 : 16 }}
                  placeholder="Enter call details..."
                  value={note}
                  onChange={(e) => {
                    setNote(e.target.value);
                    if (fieldErrors.note && e.target.value.trim()) {
                      setFieldErrors((prev) => ({ ...prev, note: false }));
                    }
                  }}
                  rows="3"
                />
                {fieldErrors.note && (
                  <div className="invalid-feedback" style={{ display: 'block', marginBottom: 8 }}>
                    Please enter a note.
                  </div>
                )}
                {/* EN and mic icon always outside textarea, with top margin for spacing */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 4 }}>
                  <button
                    type="button"
                    onClick={toggleLanguage}
                    style={{
                      padding: "2px 10px",
                      borderRadius: "12px",
                      border: "1px solid #ccc",
                      background: "#fff",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                  >
                    {speechLang === "en-IN" ? "EN" : "MR"}
                  </button>
                  <button
                    type="button"
                    onClick={startListening}
                    disabled={isRecording}
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      border: "none",
                      background: isRecording ? "#ff4d4d" : "#f1f3f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "0.3s",
                      boxShadow: isRecording
                        ? "0 0 10px 3px rgba(255, 0, 0, 0.6)"
                        : "0 2px 4px rgba(0,0,0,0.12)",
                      transform: isRecording ? "scale(1.1)" : "scale(1)",
                    }}
                  >
                    <FaMicrophone size={15} color={isRecording ? "white" : "#333"} />
                  </button>
                </div>
              </div>

              <div className="row mt-3">

                {/* Lead Type */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Lead Type</label>
                  <Select
                    options={leadTypeOptions}
                    value={leadTypeOptions.find((opt) => opt.value === leadType) || null}
                    onChange={(selected) => {
                      setLeadType(selected?.value || "");
                      if (fieldErrors.leadType && selected?.value) {
                        setFieldErrors((prev) => ({ ...prev, leadType: false }));
                      }
                    }}
                    placeholder="Select Lead Type"
                    isClearable
                    styles={{
                      ...customStyles,
                      control: (base, state) => ({
                        ...customStyles.control(base, state),
                        borderColor: fieldErrors.leadType ? '#dc3545' : base.borderColor,
                        boxShadow: fieldErrors.leadType ? '0 0 0 0.2rem rgba(220,53,69,.25)' : base.boxShadow,
                      })
                    }}
                  />
                  {fieldErrors.leadType && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      Please select Lead Type.
                    </div>
                  )}
                </div>

                {/* Call Type */}
                <div className="col-md-6 mb-3">
                  <label className="form-label">Call Type</label>
                  <Select
                    options={callTypeOptions}
                    value={callTypeOptions.find((opt) => opt.value === callType) || null}
                    onChange={(selected) => {
                      setCallType(selected?.value || "");
                      if (fieldErrors.callType && selected?.value) {
                        setFieldErrors((prev) => ({ ...prev, callType: false }));
                      }
                    }}
                    placeholder="Select Call Type"
                    isClearable
                    styles={{
                      ...customStyles,
                      control: (base, state) => ({
                        ...customStyles.control(base, state),
                        borderColor: fieldErrors.callType ? '#dc3545' : base.borderColor,
                        boxShadow: fieldErrors.callType ? '0 0 0 0.2rem rgba(220,53,69,.25)' : base.boxShadow,
                      })
                    }}
                  />
                  {fieldErrors.callType && (
                    <div className="invalid-feedback" style={{ display: 'block' }}>
                      Please select Call Type.
                    </div>
                  )}
                </div>

              </div>


              {/* 🔽 Add Follow-Up Toggle Button */}
              {!showFollowUpSection && (
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowFollowUpSection(true)}
                  >
                    + Add Follow-Up
                  </Button>
                </div>
              )}
              {/* 🟩 Follow-Up Section (Visible only after button click) */}
              {showFollowUpSection && (
                <div
                  className="mt-4 p-3 border rounded position-relative"
                  style={{ background: "#f9fafb" }}
                >
                      <button
      type="button"
      onClick={handleRemoveFollowUp}
      style={{
        position: "absolute",
        top: "10px",
        right: "12px",
        border: "none",
        background: "transparent",
        fontSize: "18px",
        cursor: "pointer",
        color: "#6c757d",
      }}
      title="Close Follow-Up"
    >
      ✖
    </button>
                  <h6 className="fw-bold text-primary mb-3">Follow-Up Details</h6>

                  <div className="mb-3">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.reason ? ' is-invalid' : ''}`}
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      required
                    />
                    {fieldErrors.reason && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        Please enter follow-up reason.
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Next Action <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${fieldErrors.next_action ? ' is-invalid' : ''}`}
                      name="next_action"
                      value={formData.next_action}
                      onChange={handleChange}
                      required
                    />
                    {fieldErrors.next_action && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        Please enter next action.
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Next Follow-Up Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className={`form-control${fieldErrors.next_follow_up_date ? ' is-invalid' : ''}`}
                      name="next_follow_up_date"
                      value={formData.next_follow_up_date}
                      onChange={handleChange}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                    {fieldErrors.next_follow_up_date && (
                      <div className="invalid-feedback" style={{ display: 'block' }}>
                        Please select next follow-up date.
                      </div>
                    )}
                  </div>

                  {/* Reminder Time Field with Clock Icon */}
                  <div className="mb-3">
                    <label htmlFor="reminderTime" className="form-label">
                    Next Follow-Up Time
                    </label>
                    <div className="position-relative">
                      <input
                        type="time"
                        className="form-control"
                        id="reminderTime"
                        name="next_followup_time"
                        value={formData.next_followup_time}
                        onChange={handleChange}
                        autoComplete="off"
                        style={{
                          paddingRight: "40px",
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                      />
                      <i
                        className="fa fa-clock position-absolute"
                        style={{
                          right: "15px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          fontSize: "18px",
                          color: "#888",
                          zIndex: 2,
                        }}
                      ></i>
                    </div>
                  </div>
                </div>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleModalClose}>
                Cancel
              </Button>

              {showFollowUpSection ? (
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    isEditing ? "Update" : "Submit"
                  )}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmitFollowUp}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    isEditing ? "Update" : "Submit"
                  )}
                </Button>
              )}
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  )
}

export default CallLogs